import React, { useState, useCallback, useMemo } from 'react';
import update from 'immutability-helper';
import TopPanel from './TopPanel';
import Content from './Content';
import { NocWrapper } from '../styled';
import ParentContext from './context';
import { ERROR_THRESHOLD_TYPE } from '@components/noc-metric-panel/utils/constants';
import type { CellData, ThresholdParams, ThresholdError } from './context';

const getUpdater = (type, config, data) => {
  return type === 'threshold_baseline' ? { $set: data } : { $merge: { [config.metric]: data } };
};

const isError = (error: ThresholdError) => {
  const baseLine = Object.values(error.threshold_baseline).some((i) => i);
  const content = Object.values(error.thresholds).some((i) => Object.values(i).some((j) => j));
  return baseLine || content;
};

const ThresholdParent = (props) => {
  const { setView } = props;
  const [thresholdConfig, updateThresholdConfig] = useState<{
    threshold_baseline: Record<string, CellData>;
    thresholds: Record<string, Record<string, CellData>>;
  }>({
    threshold_baseline: {},
    thresholds: {},
  });

  const [error, setError] = useState<ThresholdError>({
    threshold_baseline: {},
    thresholds: {},
  });

  const handleThresholdUpdate = useCallback(
    ({ configs, type, validate }: ThresholdParams) => {
      let newThresholdConfig = thresholdConfig;
      let newError = error;

      configs.forEach((config) => {
        newThresholdConfig = update(newThresholdConfig, {
          [type]: {
            [config.identifier]: (field) =>
              update(field || {}, getUpdater(type, config, config.data)),
          },
        });

        let isError = '';
        if (config.data.isCustomSelected && config.data.custom !== undefined) {
          const value = Number(config.data.custom);
          if (isNaN(value)) {
            isError = ERROR_THRESHOLD_TYPE.GENERAL;
          }

          if (!isError && validate) {
            const { type, pass } = validate(value);
            if (!pass) {
              isError = type;
            }
          }

          if (!isError && value <= 0) {
            isError = ERROR_THRESHOLD_TYPE.LESS_EQUAL_ZERO;
          }
        }

        newError = update(newError, {
          [type]: {
            [config.identifier]: (field) => update(field || {}, getUpdater(type, config, isError)),
          },
        });
      });
      updateThresholdConfig(newThresholdConfig);
      setError(newError);
    },
    [thresholdConfig, error],
  );

  const reset = useCallback(() => {
    updateThresholdConfig({
      threshold_baseline: {},
      thresholds: {},
    });
    setError({
      threshold_baseline: {},
      thresholds: {},
    });
  }, []);

  const hasError = useMemo(() => isError(error), [error]);

  const initialState = {
    thresholdConfig,
    error,
    setThresholdConfig: updateThresholdConfig,
    handleThresholdUpdate,
    reset,
    hasError,
  };

  return (
    <NocWrapper>
      <ParentContext.Provider value={initialState}>
        <TopPanel setView={setView} callback={reset} />
        <Content />
      </ParentContext.Provider>
    </NocWrapper>
  );
};

export default ThresholdParent;
